import { apiDelete, apiGet, apiPost } from './client';

/**
 * Sales-viewer grant COLLECTION transport (Phase 105, Plan 105-07).
 *
 * Mirrors `externalInvites.ts`'s shape exactly, with the project dimension
 * removed — D-01 makes the sales-viewer grant global, not project-keyed.
 * This is an INTERNAL admin surface (the grant modal on the internal Leads
 * page), NOT the `/external/*` surface from Plan 105-03 — so this module
 * uses the normal internal client (`./client`) and does not set the
 * external-audience disambiguation header that surface relies on. It also
 * never imports auth context; the caller (the modal / page) is
 * responsible for gating on `isAdmin` (D-07 — UX only, the backend
 * `requireAdmin` gate is the real control).
 */

export interface SalesViewer {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  salesViewerGrantedAt: string;
}

/**
 * List external users currently holding the sales-viewer grant.
 */
export async function listSalesViewers(): Promise<SalesViewer[]> {
  const res = await apiGet<{ data: SalesViewer[] }>('/admin/external-users/sales-viewers');
  return res.data;
}

export interface InviteOrGrantInput {
  email: string;
  /** required only when the email is new (server enforces this) */
  firstName?: string;
  lastName?: string;
}

/**
 *  - 'granted'          — existing external user, grant applied, no email sent
 *  - 'invited'          — new external user created, invite email sent, grant applied
 *  - 'already_granted'  — existing external user already held the grant — no-op
 */
export type InviteOrGrantAction = 'granted' | 'invited' | 'already_granted';

export interface InviteOrGrantResult {
  externalUserId: number;
  action: InviteOrGrantAction;
}

/**
 * Grant the sales-viewer capability to an existing or brand-new external
 * user (D-08). Mirrors `inviteOrAddExternalToProject` — server splits on
 * whether the email already maps to an active external_user.
 */
export async function inviteOrGrantSalesViewer(
  input: InviteOrGrantInput,
): Promise<InviteOrGrantResult> {
  const res = await apiPost<{ data: InviteOrGrantResult }>(
    '/admin/external-users/sales-viewers',
    input,
  );
  return res.data;
}

export interface RevokeSalesViewersResult {
  /** ids whose grant was active and is now revoked */
  revoked: number[];
}

/**
 * Revoke the sales-viewer grant for one or more external users.
 */
export async function revokeSalesViewers(
  externalUserIds: number[],
): Promise<RevokeSalesViewersResult> {
  const res = await apiDelete<{ data: RevokeSalesViewersResult }>(
    '/admin/external-users/sales-viewers',
    { externalUserIds },
  );
  return res.data;
}

export interface SalesViewerSuggestion {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
}

/**
 * Admin-only typeahead over active external users for the grant modal
 * (T-107-ENUM: kept admin-gated so it can't be used to enumerate external
 * accounts from a lower-privileged surface). Server enforces a
 * 2-character minimum on `q`; this helper short-circuits before hitting
 * the network when the query is too short, mirroring
 * `suggestExternalUsers` in `externalInvites.ts`.
 */
export async function suggestExternalUsersForGrant(q: string): Promise<SalesViewerSuggestion[]> {
  const trimmed = q.trim();
  if (trimmed.length < 2) return [];
  const params = new URLSearchParams({ q: trimmed });
  const res = await apiGet<{ data: SalesViewerSuggestion[] }>(
    `/admin/external-users/suggest?${params.toString()}`,
  );
  return res.data;
}
